import { useContext, useEffect, useMemo, useState } from 'react'

import {
  BabyGrowthCardEmbed,
  BabyGrowthCardEmbedProps,
  useHealthToolsCache,
} from '@hhgtech/hhg-components'
import {
  BabyGrowthForm,
  BabyGrowthFormProps,
} from '@hhgtech/hhg-components/babyGrowth'
import { getURLwithSSOTracking } from '@hhgtech/hhg-components/ssoV2'
import { Gender } from '@hhgtech/hhg-components/types'
import Cookies from 'js-cookie'

import { callApiNew } from 'api/axios'
import { formatBabyGrowthContent } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { Option } from 'components/molecules/dropdownSearch'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'
import { TEMP_BABY_GROWTH_KEY } from 'interfaces/constants/cookies'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { BabyGrowthContent } from 'interfaces/types'
import { AppContext } from 'state/appContext'

import { HealthToolCardEmbedProps } from '../types'

type BabyGrowthFormField = {
  name?: Option | string
  gender: Gender
  birthday: Date
  height: number
  weight: number
  head: number
  bmi: number
  measurementDate: Date
}

type CommonProps = HealthToolCardEmbedProps

export type BabyGrowthCardWrapperProps =
  | ({ onlyUseForm: true } & Partial<BabyGrowthFormProps> & CommonProps)
  | ({ onlyUseForm?: false } & Partial<BabyGrowthCardEmbedProps> & CommonProps)

export const BabyGrowthCard = ({
  onlyUseForm,
  showUserResult: _showUserResult,
  isSide,
  ...props
}: BabyGrowthCardWrapperProps) => {
  const isMobile = useIsMobile()

  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const {
    locale,
    router: { push },
  } = useRouterExtend()
  const { getHealthToolLink } = useHealthToolsCache(locale)

  const babyLink = useMemo(
    () => getHealthToolLink(HEALTH_TOOL_TYPE.BABY_GROWTH),
    [getHealthToolLink],
  )

  const [
    babyGrowthContent,
    setBabyGrowthContent,
  ] = useState<BabyGrowthContent | null>(null)
  useEffect(() => {
    ;(async () => {
      const response = await callApiNew(
        PATHS.HEALTH_TOOL.GET_HEALTH_TOOL_BABY_GROWTH_INFO,
      )
      setBabyGrowthContent(() => formatBabyGrowthContent(response?.data))
    })()
  }, [])

  const currentUrl = useCurrentUrl()

  const onSubmit = async (value: BabyGrowthFormField) => {
    const destination = `${window.location.origin}${basePath}${babyLink}result?is_embed=true`

    const payload = { ...value, name: value.name || '' }
    Cookies.set(TEMP_BABY_GROWTH_KEY, JSON.stringify(payload))
    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(destination, 'baby-growth')
    } else {
      push(
        getURLwithSSOTracking(destination, { source: 'baby-growth' })?.href ||
          destination,
      )
    }
  }

  if (!babyLink) {
    return <div style={{ height: 500 }}></div>
  }

  return onlyUseForm ? (
    <BabyGrowthForm
      onSubmit={onSubmit as any}
      formLayout={isMobile || isSide ? 'default' : 'inline'}
      submitProps={{
        dataEventLabel: currentUrl,
      }}
      {...props}
    />
  ) : (
    <BabyGrowthCardEmbed
      babyGrowthContent={babyGrowthContent as any}
      formProps={
        {
          onSubmit,
          formLayout: isMobile || isSide ? 'default' : 'inline',
          submitProps: {
            dataEventLabel: currentUrl,
          },
        } as any
      }
      {...props}
    />
  )
}
